/* eslint-disable no-useless-escape */
import dayjs from "dayjs";
import { format, parse, isValid } from "date-fns";
import axios from "axios";
import api from "../config/httpConfig";

const s3UploadClient = axios.create({
  withCredentials: false,
});

const inferFileContentType = (fileName = "") => {
  const extension = fileName.split(".").pop()?.toLowerCase();
  const mimeTypes = {
    pdf: "application/pdf",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  };
  return mimeTypes[extension] || "application/octet-stream";
};

export const DateFormatter = (date) => {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB").format(date);
};

export const formatTime = (timeInSeconds) => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = timeInSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0",
  )}`;
};

export const dealsClosesIn = (date) => {
  const now = dayjs();
  const expiryDate = dayjs(date).endOf("day");

  const diffInDays = expiryDate.diff(now, "day");

  if (diffInDays > 0) {
    return `${diffInDays} days`;
  }

  const diffInHours = expiryDate.diff(now, "hour");
  if (diffInHours > 0) {
    return `${diffInHours} hrs`;
  }

  const diffInMinutes = expiryDate.diff(now, "minute");
  return diffInMinutes > 0 ? `${diffInMinutes} mins` : "Expired";
};

export const formatTimeAgo = (timestamp) => {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return "just now";
  if (diffSec < 3600) {
    const mins = Math.floor(diffSec / 60);
    return `${mins} min${mins > 1 ? "s" : ""} ago`;
  }
  if (diffSec < 86400) {
    const hrs = Math.floor(diffSec / 3600);
    return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  }
  const days = Math.floor(diffSec / 86400);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

export const formatCurrency = (amount) => {
  const rounded = Math.round(Number(amount) || 0);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(rounded);
};

export const formatCurrencyTwoDec = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatToLacCr = (amount) => {
  if (amount >= 10000000) {
    return (amount / 10000000).toFixed(2) + " Cr"; // For Crores
  } else if (amount >= 100000) {
    return (amount / 100000).toFixed(2) + " Lac"; // For Lakhs
  }
  return new Intl.NumberFormat("en-IN").format(amount); // For smaller amounts
};

export const formatToLCr = (amount) => {
  if (amount >= 10000000) {
    return (amount / 10000000).toFixed(0) + " Cr"; // For Crores
  } else if (amount >= 100000) {
    return (amount / 100000).toFixed(0) + " L"; // For Lakhs
  }
  return new Intl.NumberFormat("en-IN").format(amount); // For smaller amounts
};

export const truncateDescription = (description, limit) => {
  if (description?.length <= limit) {
    return description;
  }
  return `${description?.slice(0, limit)}...`;
};

export const calculateSoldOutPercentage = (fundedAmt, approvedFundAmt) => {
  return ((Number(fundedAmt) / Number(approvedFundAmt)) * 100).toFixed(0);
};

export const calculateReturns = (annualDiscountRate) => {
  return (Number(annualDiscountRate) * 100).toFixed(0);
};

export const calculateDiscountAmount = (
  dealAmount,
  annualDiscountRate,
  tenure,
) => {
  return (
    (Number(dealAmount) * Number(annualDiscountRate) * Number(tenure)) /
    365
  ).toFixed(2);
};

export const calculateAvailableAmountToInvest = (
  approvedFundAmt,
  fundedAmt,
) => {
  return (Number(approvedFundAmt) - Number(fundedAmt)).toFixed(2);
};

export const calculateAmountToInvestNow = (dealAmount, discountAmount) => {
  return (Number(dealAmount) - Number(discountAmount)).toFixed(2);
};

export const calculateNetReturns = (
  discountAmount,
  platformFee,
  penalCharge,
) => {
  return (
    Number(discountAmount) -
    (Number(platformFee) + Number(penalCharge))
  ).toFixed(2);
};

export const calculatePlatformFee = (dealAmount, platformFee, tenure) => {
  return (
    ((Number(dealAmount) * Number(platformFee)) / 365) *
    Number(tenure)
  ).toFixed(2);
};

export const calculateExpectedNetProfit = (discountAmount, platformFee) => {
  return (Number(discountAmount) - Number(platformFee)).toFixed(2);
};

export const calculateExpectedNetRepayment = (
  amountToInvestNow,
  discountAmount,
  platformFee,
) => {
  return (
    Number(amountToInvestNow) +
    (Number(discountAmount) - Number(platformFee))
  ).toFixed(2);
};

export const calculateDateRange = (filterId) => {
  const now = dayjs();
  let start, end;

  switch (filterId) {
    case "ORV0001": // Till Date
      start = "";
      end = "";
      break;
    case "ORV0002": // Last Month
      start = now.subtract(1, "month").startOf("month").format("YYYY-MM-DD");
      end = now.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      break;
    case "ORV0003": // Last 3 months
      start = now.subtract(3, "months").startOf("month").format("YYYY-MM-DD");
      end = now.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      break;
    case "ORV0004": // Last 6 months
      start = now.subtract(6, "months").startOf("month").format("YYYY-MM-DD");
      end = now.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      break;
    case "ORV0005": // Last 12 months
      start = now.subtract(12, "months").startOf("month").format("YYYY-MM-DD");
      end = now.subtract(1, "month").endOf("month").format("YYYY-MM-DD");
      break;
    default:
      start = "";
      end = "";
      break;
  }

  return { start, end };
};

export const formatCategoryName = (category) => {
  if (category.toLowerCase() === "roi") {
    return category.toUpperCase();
  }

  if (category === "industryTypes") {
    return "Industries";
  }

  return category
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatOptionLabel = (category, label) => {
  // Append "%" for ROI and "days" for Maturity
  if (category === "roi") {
    return `${label}%`;
  }
  if (category === "maturity") {
    return `${label} days`;
  }
  return label;
};

export const maskEmail = (email) => {
  if (!email) return "";
  const [localPart, domain] = email.split("@");
  return localPart[0] + "****" + localPart.slice(-1) + "@" + domain;
};

export const maskPhone = (phone) => {
  if (!phone) return "";
  return phone.slice(0, 4) + "****" + phone.slice(-2);
};

export const capitalizeWords = (str) => {
  return str
    ?.replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char?.toUpperCase());
};

export const capitalizeEntireWord = (str) => {
  return str
    ?.replace(/_/g, " ")
    .replace(/\b\w+\b/g, (word) => word.toUpperCase());
};

export const formatParticulars = (str) => {
  if (!str) return "";
  return str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const toTitleCase = (sentence) => {
  if (!sentence || typeof sentence !== "string") return "";

  return sentence
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatDateTime = () => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const year = now.getFullYear();

  let hours = now.getHours();
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert 0 to 12-hour format
  const formattedTime = `${hours}:${minutes}_${ampm}`;

  const formattedDateTime = `${day}-${month}-${year}_${formattedTime}`;
  return formattedDateTime;
};

export const parseUnknownDate = (inputDate) => {
  const possibleFormats = [
    "yyyy-MM-dd", // ISO 8601
    "dd-MM-yyyy", // European format
    "MM/dd/yyyy", // US format
    "yyyy/MM/dd", // Slash-separated ISO
    "dd/MM/yyyy", // Slash-separated European
    "MMMM d, yyyy", // Full month, day, year
    "d MMMM yyyy", // Day full month year
    "yyyy-MM-dd'T'HH:mm:ssX", // ISO 8601 with time
  ];

  let reqFormat = inputDate;

  // Handle two-digit years (MM/DD/YY)
  const regex = /^(\d{1,2})\/(\d{1,2})\/(\d{2})(?!\d)/;
  if (regex.test(reqFormat)) {
    reqFormat = reqFormat.replace(regex, (match, mm, dd, yy) => {
      const fullYear = parseInt(yy, 10) > 50 ? `19${yy}` : `20${yy}`;
      return `${mm}/${dd}/${fullYear}`;
    });
  }

  for (const formatString of possibleFormats) {
    const parsedDate = parse(reqFormat, formatString, new Date());
    if (isValid(parsedDate)) {
      return format(parsedDate, "yyyy-MM-dd");
    }
  }

  // Return null if no format matches
  return null;
};

export async function handleDeleteFile(fileUrl) {
  const fileKey = fileUrl.split(".com/")[1];

  const res = await api.delete("/api/fileDelete", {
    data: { fileKey },
  });

  return res.data;
}

export async function handleUploadFile(file, path, onProgress) {
  const contentType = file.type || inferFileContentType(file.name);
  const res = await api.post("/api/fileUpload", {
    fileName: file.name,
    fileType: contentType,
    folderPath: path,
  });
  const { uploadUrl, fileUrl } = res;

  const fileName = file.name || `merged_${Date.now()}.pdf`;

  await s3UploadClient.put(uploadUrl, file, {
    headers: {
      "Content-Type": contentType,
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        onProgress(percentCompleted);
      }
    },
  });

  return { fileUrl, fileName };
}

export const confirmPasswordRules = [
  ({ getFieldValue }) => ({
    validator(_, value) {
      if (!value)
        return Promise.reject(new Error("Please confirm your Password!"));
      if (getFieldValue("enterPassword") !== value)
        return Promise.reject(new Error("Passwords do not match!"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  }),
];

export const passwordRules = [
  {
    required: true,
    message: "Password cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 8)
        return Promise.reject(
          new Error("Password must be at least 8 characters!"),
        );
      if (!/[A-Z]/.test(value))
        return Promise.reject(
          new Error("Password must contain at least one uppercase letter!"),
        );
      if (!/[a-z]/.test(value))
        return Promise.reject(
          new Error("Password must contain at least one lowercase letter!"),
        );
      if (!/\d/.test(value))
        return Promise.reject(
          new Error("Password must contain at least one number!"),
        );
      if (!/[@$!%*?&]/.test(value))
        return Promise.reject(
          new Error("Password must contain at least one special character!"),
        );
      if (value.length > 256)
        return Promise.reject(
          new Error("Password cannot exceed 256 characters!"),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const phoneNumberRules = (type) => [
  {
    required: true,
    message: `${type} Phone Number cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^[6-9]\d{9}$/.test(value))
        return Promise.reject(
          new Error(`Please enter a valid ${type} Phone Number`),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const ifscRules = [
  {
    required: true,
    message: "IFSC code cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^[A-Z]{4}[0][A-Z0-9]{6}$/.test(value))
        return Promise.reject(new Error("Please enter a valid IFSC code"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const accNumberRules = [
  {
    required: true,
    message: "Account number cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 8 || value.length > 16)
        return Promise.reject(new Error("Please enter a valid account number"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const nameRules = (type) => [
  {
    required: true,
    whitespace: true,
    message: `${type} Name cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      const trimmed = typeof value === "string" ? value.trim() : value;
      if (!trimmed) {
        return value
          ? Promise.reject(new Error(`${type} Name cannot be empty!`))
          : Promise.resolve();
      }
      if (trimmed.length < 3)
        return Promise.reject(
          new Error(`${type} Name must be at least 3 characters!`),
        );
      if (trimmed.length > 256)
        return Promise.reject(
          new Error(`${type} Name cannot exceed 256 characters!`),
        );
      if (!/^[a-zA-Z\s]+$/.test(value))
        return Promise.reject(
          new Error(`${type} Name can only contain alphabets and spaces.`),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const emailRules = (type) => [
  {
    required: true,
    message: `${type} Email cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length > 256)
        return Promise.reject(
          new Error(`${type} Email cannot exceed 256 characters!`),
        );
      if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(value))
        return Promise.reject(new Error(`Please enter a valid ${type} Email.`));
      const localPart = value.split("@")[0];
      if (!/[A-Za-z]/.test(localPart)) {
        const emailLabel = type ? `${type} Email` : "Email";
        return Promise.reject(
          new Error(`${emailLabel} cannot contain only numbers`),
        );
      }
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const companyNameRules = (type) => [
  {
    required: true,
    message: `${type} name cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 3)
        return Promise.reject(
          new Error(`${type} name must be at least 3 characters!`),
        );
      if (value.length > 256)
        return Promise.reject(
          new Error(`${type} name cannot exceed 256 characters!`),
        );
      if (/^\d+$/.test(value))
        return Promise.reject(
          new Error(`${type} name cannot contain only numbers!`),
        );
      if (/^[^a-zA-Z0-9]+$/.test(value))
        return Promise.reject(
          new Error(`${type} name cannot contain only special characters!`),
        );
      if (!/[a-zA-Z]/.test(value))
        return Promise.reject(
          new Error(`${type} name must include at least one letter!`),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const designationRules = [
  {
    required: true,
    message: "Designation cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 2)
        return Promise.reject(
          new Error("Designation must be at least 2 characters!"),
        );
      if (value.length > 256)
        return Promise.reject(
          new Error("Designation cannot exceed 256 characters!"),
        );
      if (!/^[a-zA-Z\s]+$/.test(value))
        return Promise.reject(
          new Error("Designation can only contain alphabets and spaces."),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const address1Rules = [
  {
    required: true,
    message: "Address Line 1 cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 3)
        return Promise.reject(
          new Error("Address Line 1 must be at least 3 characters!"),
        );
      if (value.length > 256)
        return Promise.reject(
          new Error("Address Line 1 cannot exceed 256 characters!"),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const address2Rules = [
  {
    required: true,
    message: "Address Line 2 cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (value.length < 3)
        return Promise.reject(
          new Error("Address Line 2 must be at least 3 characters!"),
        );
      if (value.length > 256)
        return Promise.reject(
          new Error("Address Line 2 cannot exceed 256 characters!"),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const cityRules = [
  {
    required: true,
    message: "City cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^[A-Za-z\s]+$/.test(value))
        return Promise.reject(new Error("City should contain only Alphabets."));
      if (value.length < 3)
        return Promise.reject(new Error("City must be at least 3 characters!"));
      if (value.length > 256)
        return Promise.reject(new Error("City cannot exceed 256 characters!"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const stateRules = [
  {
    required: true,
    message: "State cannot be empty!",
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^[A-Za-z\s]+$/.test(value))
        return Promise.reject(new Error("State should contain only Alphabets."));
      if (value.length < 3)
        return Promise.reject(new Error("State must be at least 3 characters!"));
      if (value.length > 256)
        return Promise.reject(new Error("State cannot exceed 256 characters!"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const pincodeRules = [
  {
    required: true,
    message: `Pincode cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^\d+$/.test(value))
        return Promise.reject(
          new Error("Pincode should contain only numbers."),
        );
      if (value.length !== 6)
        return Promise.reject(new Error("Pincode should be exactly 6 digits."));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const entNameRules = [
  {
    required: true,
    message: `Enterprise Name cannot be empty!`,
  },
];

export const tenureRules = [
  {
    required: true,
    message: `Tenure cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^\d+$/.test(value))
        return Promise.reject(new Error("Tenure should contain only numbers."));
      if (value < 30 || value > 90)
        return Promise.reject(
          new Error("Tenure must be between 30 and 90 days."),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const resolveMaxFundRequiredPct = (value) => {
  const pct = Number(value);
  if (!Number.isFinite(pct) || pct < 0.6 || pct > 0.8) return 0.8;
  return pct;
};

export const maxFundAllowedAmount = (totalInv, maxFundRequiredPct = 0.8) => {
  const pctBasisPoints = Math.round(resolveMaxFundRequiredPct(maxFundRequiredPct) * 10000);
  return Math.floor((Number(totalInv) * pctBasisPoints) / 10000);
};

export const fundReqRules = (totalInv, maxFundRequiredPct = 0.8) => [
  {
    validator(_, value) {
      const resolvedPct = resolveMaxFundRequiredPct(maxFundRequiredPct);
      const maxAllowed = maxFundAllowedAmount(totalInv, resolvedPct);
      const pctLabel = parseFloat((resolvedPct * 100).toFixed(2));
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }
      if (value < 80000) {
        return Promise.reject("Minimum funding amount is ₹80,000");
      }
      if (value > maxAllowed) {
        return Promise.reject(
          `Cannot exceed ${pctLabel}% of Total Invoice Value (₹${maxAllowed})`,
        );
      }
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const panRules = [
  {
    required: true,
    message: `PAN cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value))
        return Promise.reject(new Error("Please enter a valid PAN"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const gstinRules = [
  {
    required: true,
    message: `GSTIN cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (
        !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(value)
      )
        return Promise.reject(new Error("Please enter a valid GSTIN"));
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const websiteRules = [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.resolve();
      }
      const urlPattern = /^(https?:\/\/)?([\w\-])+(\.[\w\-]+)+[/#?]?.*$/i;
      if (!urlPattern.test(value)) {
        return Promise.reject(
          "Enter a valid website URL (e.g.example.com).",
        );
      }
      if (value.length > 250) {
        return Promise.reject("Website URL cannot exceed 250 characters.");
      }
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const businessDescriptionRules = [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject("Business Description cannot be empty!");
      }

      if (value.length > 500) {
        return Promise.reject(
          "Business Description cannot exceed 250 characters.",
        );
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const FinancialSummaryRules = [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject("Financial Summary cannot be empty!");
      }

      if (value.length > 500) {
        return Promise.reject(
          "Finalcial Summary cannot exceed 250 characters.",
        );
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const CommentsRules = [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject("Comments cannot be empty!");
      }

      if (value.length > 500) {
        return Promise.reject("Comments cannot exceed 250 characters.");
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const dealRules = (type) => [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject(`${type} cannot be empty!`);
      }

      if (value.length > 1000) {
        return Promise.reject(`${type} cannot exceed 1000 characters.`);
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const addinfoRules = (type) => [
  {
    validator(_, value) {
      if (value.length > 1000) {
        return Promise.reject(`${type} cannot exceed 1000 characters.`);
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const fundReqJvRules = [
  {
    required: true,
    message: "Fund Required cannot be empty!",
  },
  ({ getFieldValue }) => ({
    validator(_, value) {
      const projectValue = getFieldValue("projectValue");

      // Skip validation if empty (required rule handles it)
      if (
        value === undefined ||
        value === null ||
        value === ""
      ) {
        return Promise.resolve();
      }

      const num =
        typeof value === "string"
          ? Number(value.replace(/[^\d.]/g, ""))
          : Number(value);

      // Minimum amount validation
      if (num < 200000) {
        return Promise.reject(
          new Error("Fund Required must be at least ₹2,00,000")
        );
      }

      const parsedProjectVal =
        projectValue !== undefined &&
        projectValue !== null &&
        projectValue !== ""
          ? typeof projectValue === "string"
            ? Number(projectValue.replace(/[^\d.]/g, ""))
            : Number(projectValue)
          : null;

      // Cannot exceed Project Value
      if (
        parsedProjectVal !== null &&
        !isNaN(parsedProjectVal) &&
        num > parsedProjectVal
      ) {
        return Promise.reject(
          new Error(
            "Fund Required cannot exceed Project Value"
          )
        );
      }

      return Promise.resolve();
    },
  }),
];

export const isValidMeaningfulData = (value) => {
  const trimmedValue = String(value || "").trim();
  if (!trimmedValue) return true;

  if (/(.)(\1{4,})/.test(trimmedValue)) return false;

  const specialCharCount = (trimmedValue.match(/[^a-zA-Z0-9\s]/g) || []).length;
  if (specialCharCount > trimmedValue.length * 0.4) return false;

  const letterCount = (trimmedValue.match(/[a-zA-Z]/g) || []).length;
  if (letterCount < 2) return false;

  return true;
};

export const projectNameJvRules = [
  { required: true, message: "Please enter project name" },
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();

      const trimmedValue = String(value).trim();
      if (!trimmedValue) return Promise.reject(new Error("Please enter project name"));
      if (!isValidMeaningfulData(trimmedValue)) return Promise.reject(new Error("Please enter a valid and meaningful data"));
      if (trimmedValue.length < 3) return Promise.reject(new Error("Project name must be at least 3 characters"));
      if (trimmedValue.length > 100) return Promise.reject(new Error("Project name cannot exceed 100 characters"));
      if (!/[A-Za-z]/.test(trimmedValue)) return Promise.reject(new Error("Project name must contain at least one alphabet"));

      return Promise.resolve();
    },
  },
];

export const projectDescriptionJvRules = [
  {
    validator: (_, value) => {
      const rawValue = String(value || "");
      const trimmedValue = rawValue.trim();

      if (!trimmedValue) return Promise.reject(new Error("Please provide detailed project description"));
      if (!isValidMeaningfulData(trimmedValue)) return Promise.reject(new Error("Please enter a valid and meaningful data"));
      if (trimmedValue.length < 4) return Promise.reject(new Error("Project description must be at least 4 characters"));
      if (rawValue.length > 500) return Promise.reject(new Error("Project description cannot exceed 500 characters"));
      if (/<[^>]*>/.test(trimmedValue)) return Promise.reject(new Error("Project description cannot contain HTML or script tags"));
      if (!/[A-Za-z]/.test(trimmedValue)) return Promise.reject(new Error("Project description must contain at least one alphabet"));

      return Promise.resolve();
    },
  },
];

const validateJvLongTextField = (value, options = {}) => {
  const { required = true, label = "This field" } = options;
  const rawValue = String(value || "");
  const trimmedValue = rawValue.trim();

  if (!trimmedValue) {
    return required
      ? Promise.reject(new Error(`Please enter ${label}`))
      : Promise.resolve();
  }

  if (!isValidMeaningfulData(trimmedValue)) return Promise.reject(new Error("Please enter a valid and meaningful data"));
  if (trimmedValue.length < 4) return Promise.reject(new Error(`${label} must be at least 4 characters`));
  if (rawValue.length > 500) return Promise.reject(new Error(`${label} cannot exceed 500 characters`));
  if (/<[^>]*>/.test(trimmedValue)) return Promise.reject(new Error(`${label} cannot contain HTML or script tags`));
  if (!/[A-Za-z]/.test(trimmedValue)) return Promise.reject(new Error(`${label} must contain at least one alphabet`));

  return Promise.resolve();
};

const createJvLongTextRules = (options = {}) => [
  {
    validator: (_, value) => validateJvLongTextField(value, options),
  },
];

export const investmentStructureJvRules = createJvLongTextRules({
  required: true,
  label: "Investment structure",
});

export const profitSharingModelJvRules = createJvLongTextRules({
  required: true,
  label: "Revenue / profit sharing model",
});

export const riskFactorsJvRules = createJvLongTextRules({
  required: false,
  label: "Risk factors",
});

export const projectDescRules = [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject("Project description cannot be empty!");
      }

      const trimmedValue = value.trim();

      // Only numbers not allowed
      if (/^\d+$/.test(trimmedValue)) {
        return Promise.reject(
          "Project description cannot contain only numbers!"
        );
      }

      // Only special characters not allowed
      if (/^[^a-zA-Z0-9]+$/.test(trimmedValue)) {
        return Promise.reject(
          "Project description cannot contain only special characters!"
        );
      }

      // Max length validation
      if (trimmedValue.length > 1000) {
        return Promise.reject(
          "Project description cannot exceed 1000 characters."
        );
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const projectDurationRules = [
  {
    required: true,
    message: `Project duration cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (value === undefined || value === null || value === "") return Promise.resolve();
      if (!/^\d+$/.test(String(value)))
        return Promise.reject(
          new Error("Project duration should contain only numbers."),
        );
      const num = Number(value);
      if (num < 1)
        return Promise.reject(
          new Error("Project duration must be at least 1 month"),
        );
      if (num > 200)
        return Promise.reject(
          new Error("Project duration should not exceed 200 months"),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const tenureJvRules = [
  {
    required: true,
    message: "Please enter tenure",
  },
  ({ getFieldValue }) => ({
    validator(_, value) {
      const projectDuration = getFieldValue("projectDuration");
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }

      const num = Number(value);
      if (num < 1) {
        return Promise.reject(new Error("Tenure must be at least 1 month"));
      }
      if (num > 200) {
        return Promise.reject(new Error("Tenure should not exceed 200 months"));
      }
      if (projectDuration && num > Number(projectDuration)) {
        return Promise.reject(
          new Error("Tenure should not exceed project duration"),
        );
      }

      return Promise.resolve();
    },
  }),
];

export const expectedRoiJvRules = [
  { required: true, message: "Please enter expected ROI" },
  {
    validator: (_, value) => {
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }

      const num = Number(value);
      if (num <= 0) {
        return Promise.reject(new Error("ROI must be greater than 0"));
      }
      if (num >= 100) {
        return Promise.reject(new Error("ROI should be less than 100%"));
      }

      return Promise.resolve();
    },
  },
];

export const profitShareJvRules = [
  { required: true, message: "Please enter profit share" },
  {
    validator: (_, value) => {
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }

      const num = Number(value);
      if (num < 0) {
        return Promise.reject(new Error("Profit share cannot be negative"));
      }
      if (num > 100) {
        return Promise.reject(new Error("Profit share should not exceed 100%"));
      }

      return Promise.resolve();
    },
  },
];

export const percentRules = (type) => [
  {
    required: true,
    message: `${type} cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator: (_, value) => {
      if (value === undefined || value === null || value === "")
        return Promise.resolve();
      const num = Number(value);
      if (num < 0)
        return Promise.reject(new Error(`${type} should not be negative.`));
      if (num > 100)
        return Promise.reject(
          new Error(`${type} should not be greater than 100.`),
        );
      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const getDownloadSuccessMessage = (fileName) => `${fileName} downloaded successfully`;

export const descriptionRules = (type) => [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject(`${type} Description cannot be empty!`);
      }

      if (value.length > 500) {
        return Promise.reject(
          `${type} Description cannot exceed 500 characters.`,
        );
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const financialSummaryRules = [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject("Financial Summary cannot be empty!");
      }

      if (value.length > 500) {
        return Promise.reject(
          "Financial Summary cannot exceed 500 characters.",
        );
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const reasonRules = [
  {
    validator(_, value) {
      if (!value || value.trim() === "") {
        return Promise.reject("Reason cannot be empty!");
      }

      if (value.length > 500) {
        return Promise.reject("Reason cannot exceed 500 characters.");
      }

      return Promise.resolve();
    },
    validateTrigger: ["onBlur", "onChange"],
  },
];

export const downloadBlob = (data, fileName) => {
  const blob = data instanceof Blob ? data : new Blob([data]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const scTenureRules = [
  {
    required: true,
    message: `Tenure cannot be empty!`,
    validateTrigger: "onBlur",
  },
  {
    validator(_, value) {
      if (!value) return Promise.resolve();
      if (!/^\d+$/.test(value))
        return Promise.reject(new Error("Tenure should contain only numbers."));
      if (value < 30 || value > 120) {
        return Promise.reject(new Error("Tenure must be between 30 and 120 days."));
      }
      return Promise.resolve();
    },
  },
];
